import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  LayoutGrid, PlusCircle, Ticket as TicketIcon, Bell, 
  User, KeyRound, LogOut, ChevronLeft, ChevronRight, Menu, X, Loader2
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/theme-toggle';
import { UserDashboard } from '@/components/dashboard/UserDashboard';
import { UserTicketList } from '@/components/dashboard/UserTicketList';
import { UserTicketDetail } from '@/components/dashboard/UserTicketDetail';
import { UserNotifications } from '@/components/dashboard/UserNotifications';
import { UserProfile } from '@/components/dashboard/UserProfile';
import { CreateTicket } from '@/components/console/CreateTicket';
import { ChangePasswordModal } from '@/components/auth/ChangePasswordModal';
import { ticketsApi } from '@/api/ticketsApi';
import { mapRow } from '@/lib/data';
import type { Ticket } from '@/lib/mock';
import { useNow } from '@/lib/useNow';

export function NormalUserPortal() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { ticketId: paramTicketId } = useParams<{ ticketId: string }>();
  const now = useNow(5000);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('requester_sidebar_collapsed') === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Tickets state
  const [myTickets, setMyTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(paramTicketId || null);
  const [loading, setLoading] = useState(true);

  const toggleSidebar = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('requester_sidebar_collapsed', String(next));
      return next;
    });
  };

  // Fetch only this requester's tickets from backend API
  const loadMyTickets = useCallback(async () => {
    try {
      setLoading(true);
      const rawTickets = await ticketsApi.getMyTickets();
      if (Array.isArray(rawTickets)) {
        const mapped = rawTickets.map(mapRow);
        setMyTickets(mapped);
      }
    } catch (err) {
      console.error('Failed to load my tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMyTickets();
  }, [loadMyTickets]);

  // Listen for ticket changes
  useEffect(() => {
    const handleSync = () => loadMyTickets();
    window.addEventListener('compass_tickets_changed', handleSync);
    return () => window.removeEventListener('compass_tickets_changed', handleSync);
  }, [loadMyTickets]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const currentPath = location.pathname;
  const isDashboard = currentPath === '/user' || currentPath === '/user/dashboard';
  const isTickets = currentPath === '/user/tickets' || currentPath === '/my-tickets';
  const isNotifications = currentPath === '/user/notifications';
  const isProfile = currentPath === '/user/profile';

  const selectedTicket = selectedTicketId ? myTickets.find(t => t.id === selectedTicketId) : null;

  const handleCreateTicket = async (newTicketData: any) => {
    try {
      const created = await ticketsApi.createTicket({
        ...newTicketData,
        source_type: 'USER_CREATED',
        requester_id: user?.id,
        requester_email: user?.email,
        requester_name: user?.displayName || user?.name
      });
      setCreateTicketOpen(false);
      await loadMyTickets();
      window.dispatchEvent(new CustomEvent('compass_tickets_changed'));
      if (created?.ticket?.id) {
        const ticketRef = created.ticket.number || created.ticket.ref || created.ticket.id;
        setSelectedTicketId(created.ticket.id);
        navigate(`/tickets/${ticketRef}`);
      }
      return { success: true, ticket: created?.ticket };
    } catch (err: any) {
      console.error('Failed to create ticket:', err);
      return { success: false, error: err.message };
    }
  };

  const handleUpdateTicketComment = async (content: string) => {
    if (!selectedTicketId) return;
    try {
      await ticketsApi.addComment(selectedTicketId, content, false); // strictly public comment
      await loadMyTickets();
    } catch (err) {
      console.error('Failed to add public comment:', err);
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs lg:hidden transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ROLE-BASED SIDEBAR (Normal User) */}
      <aside 
        style={{ 
          width: collapsed ? '72px' : '260px', 
          minWidth: collapsed ? '72px' : '260px',
          maxWidth: collapsed ? '72px' : '260px'
        }}
        className={cn(
          "flex flex-col border-r border-border bg-card shrink-0 h-screen transition-all duration-200 ease-in-out z-30",
          mobileOpen ? "fixed inset-y-0 left-0" : "hidden lg:flex"
        )}
      >
        {/* Brand Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md">
              <TicketIcon className="h-5 w-5" />
            </div>
            {!collapsed && (
              <div className="flex flex-col overflow-hidden">
                <span className="font-bold text-sm tracking-tight truncate">Support Portal</span>
                <span className="text-[11px] text-muted-foreground font-medium truncate">Compass Helpdesk</span>
              </div>
            )}
          </div>

          <button 
            onClick={toggleSidebar}
            className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>

          <button 
            onClick={() => setMobileOpen(false)}
            className="lg:hidden h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
          <div>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Helpdesk Menu
              </p>
            )}
            <nav className="space-y-1">
              <button
                onClick={() => { setSelectedTicketId(null); navigate('/user/dashboard'); }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                  isDashboard && !selectedTicketId ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "Dashboard" : undefined}
              >
                <LayoutGrid className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Dashboard</span>}
              </button>

              <button
                onClick={() => setCreateTicketOpen(true)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-primary bg-primary/5 hover:bg-primary/15",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "Create New Ticket" : undefined}
              >
                <PlusCircle className="h-4 w-4 shrink-0 text-primary" />
                {!collapsed && <span>Create New Ticket</span>}
              </button>

              <button
                onClick={() => { setSelectedTicketId(null); navigate('/user/tickets'); }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                  isTickets && !selectedTicketId ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "My Tickets" : undefined}
              >
                <TicketIcon className="h-4 w-4 shrink-0" />
                {!collapsed && <span>My Tickets</span>}
                {!collapsed && myTickets.length > 0 && (
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-muted font-semibold">
                    {myTickets.length}
                  </span>
                )}
              </button>

              <button
                onClick={() => navigate('/user/notifications')}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                  isNotifications ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "Notifications" : undefined}
              >
                <Bell className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Notifications</span>}
              </button>

              <button
                onClick={() => navigate('/user/profile')}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                  isProfile ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "Profile" : undefined}
              >
                <User className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Profile</span>}
              </button>

              <button
                onClick={() => setChangePasswordOpen(true)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-muted-foreground hover:bg-muted hover:text-foreground",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "Change Password" : undefined}
              >
                <KeyRound className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Change Password</span>}
              </button>
            </nav>
          </div>
        </div>

        {/* User Card & Sign Out */}
        <div className="p-3 border-t border-border mt-auto">
          {!collapsed && (
            <div className="flex items-center gap-3 mb-3 p-2 rounded-xl bg-muted/40">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold shrink-0">
                {(user?.displayName || user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="overflow-hidden min-w-0">
                <p className="text-xs font-semibold truncate">{user?.displayName || user?.name || user?.email}</p>
                <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          )}

          <button
            onClick={handleSignOut}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors",
              collapsed && "justify-center px-0"
            )}
            title={collapsed ? "Sign Out" : undefined}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* MAIN VIEWPORT */}
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Mobile Topbar */}
        <div className="lg:hidden flex h-14 items-center justify-between px-4 border-b border-border bg-card">
          <button 
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-md hover:bg-muted"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-bold text-sm">Compass Support</span>
          <ThemeToggle />
        </div>

        {/* Content Container */}
        <div className="flex-1 overflow-hidden relative">
          {loading && myTickets.length === 0 ? (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : selectedTicket ? (
            <div className="h-full flex flex-col">
              <div className="p-3 border-b border-border flex items-center gap-2 bg-muted/20">
                <button
                  onClick={() => setSelectedTicketId(null)}
                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <ChevronLeft className="h-4 w-4" /> Back to My Tickets
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <UserTicketDetail 
                  ticket={selectedTicket}
                  now={now}
                  actions={{
                    onStatus: () => {},
                    onUpdate: (body) => handleUpdateTicketComment(body),
                    onAddFile: () => {},
                    onResolve: () => {}
                  }}
                />
              </div>
            </div>
          ) : isTickets ? (
            <UserTicketList 
              tickets={myTickets}
              onSelectTicket={(id) => setSelectedTicketId(id)}
              onNewTicket={() => setCreateTicketOpen(true)}
            />
          ) : isNotifications ? (
            <UserNotifications />
          ) : isProfile ? (
            <UserProfile />
          ) : (
            <UserDashboard 
              tickets={myTickets}
              onNewTicket={() => setCreateTicketOpen(true)}
              onSelectTicket={(id) => setSelectedTicketId(id)}
              onViewAll={() => navigate('/user/tickets')}
            />
          )}
        </div>
      </main>

      {/* Create Ticket Dialog */}
      <CreateTicket
        open={createTicketOpen}
        initialTeamId="11111111-1111-1111-1111-111111111111"
        onClose={() => setCreateTicketOpen(false)}
        onCreate={handleCreateTicket}
        onSelectTicket={(id) => {
          setSelectedTicketId(id);
          setCreateTicketOpen(false);
          navigate('/user/tickets');
        }}
      />

      {/* Change Password Dialog */}
      <ChangePasswordModal 
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}
